import { useEffect } from 'react';
import { selectorFamily, useRecoilValue, } from 'recoil';
import { useFieldRef } from '../../contexts/refs';
import { alwaysValidValidationSelector } from '../../recoil';
import { useValidatedState } from '../useValidatedState';
import { useValidationError } from '../useValidationError';
import { useI18nCmsField } from './useI18nCmsField';
import { useMuiFieldRecoilState } from './useMuiField';
export const neverDisabledSelectorFamily = selectorFamily({
    key: 'common/neverDisabled',
    get: (_fieldName) => () => false,
});
/**
 *
 * @returns [props, saveValue, setValidationValue]
 * - props => props to use with component
 * - saveValue => save to recoil atom
 * - setValidationValue => run through local yumSchema for validation without saving
 */
export function useFieldBase({ atom, fieldName, disabled, disabledSelector = neverDisabledSelectorFamily, required, validationSelector = alwaysValidValidationSelector, validationEffects, i18nVariables, i18nFieldNameOverride, i18nPrefix, defaultValue, }) {
    // push to global state
    const [state, setState] = useMuiFieldRecoilState(atom);
    // get ref to field
    const ref = useFieldRef(fieldName);
    // get error message
    const { validationError, invokeValidation } = useValidationError(validationSelector, validationEffects, i18nFieldNameOverride ?? fieldName, i18nVariables, i18nPrefix);
    // check if field should be disabled
    const forceDisabled = useRecoilValue(disabledSelector(fieldName));
    const disabledState = disabled || forceDisabled;
    // i18n values
    const i18nProps = useI18nCmsField(i18nFieldNameOverride ?? fieldName, i18nVariables, i18nPrefix);
    // set default value
    useEffect(() => {
        if (defaultValue !== undefined) {
            if (typeof defaultValue === 'function') {
                // default by callback function for special rules
                // FIXME: should be possible with function signature overrides
                const val = defaultValue(state);
                setState(val);
            }
            else if (state === null) {
                // default by plain T
                setState(defaultValue);
            }
        }
        // we only want the default value to be set once the
        // hook renders for the first time on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return [
        {
            ...i18nProps,
            error: !!validationError && !disabledState,
            helperText: validationError,
            ref,
            disabled: disabledState,
            // we currently only pass required, but might add a requiredSelector at some point
            required: required ?? false,
            value: state,
        },
        setState,
        invokeValidation,
    ];
}
/**
 *
 * @param param0
 * @returns [props, saveValue, setValidationValue]
 * - props => props to use with component
 * - saveValue => save to recoil atom
 * - setValidationValue => run through local yupSchema for validation without saving
 */
export function useFieldLocalYupValidationBase({ initialState, yupSchema, ...other }) {
    // for local field validation
    const [value, setValue] = useValidatedState(initialState, yupSchema);
    const [{ value: state, ...baseProps }, saveState] = useFieldBase(other);
    // grab value changes from global state
    useEffect(() => {
        if (state !== undefined && state !== null) {
            setValue(state);
        }
        // clear if we get null (e.g. reset) from recoil
        if (state === null) {
            setValue(initialState);
        }
    }, [state, setValue, initialState]);
    return [
        {
            ...baseProps,
            value,
        },
        saveState,
        setValue,
    ];
}
export function useInputField({ mapInput, ...props }) {
    // get base props of validation fields
    const [baseProps, saveState, setValidationValue] = useFieldLocalYupValidationBase({
        ...props,
        initialState: '',
    });
    return {
        ...baseProps,
        onBlur: (e) => {
            saveState(mapInput(e.target.value));
        },
        onChange: (e) => {
            setValidationValue(e.target.value);
        },
    };
}
// TODO: for backwards compatibility, can be removed once all hooks are renamed
export const useValidationFieldBase = useFieldBase;
export const useValidationField = useInputField;
export const useValidationFieldLocalYupValidationBase = useFieldLocalYupValidationBase;
